import { useEffect, useState } from "react";
import { Box, Grid, GridItem, Input, FormControl, FormLabel, Select, Button, FormErrorMessage } from "@chakra-ui/react";
import MyDiv from "./identity.style";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CustomToast } from "../../../../../components";
import { useFetch } from "../../../../../hooks/useFetch";
import { IdentityBankForm } from "../../modal";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";

interface IdentityBankProps {
  nextStep: () => void;
  prevStep: () => void;
  identity: IdentityBankForm;
  setIdentity: React.Dispatch<React.SetStateAction<IdentityBankForm>>;
  employeeId: number | null;
}

interface BOption {
  id: number;
  bank_Name: string;

}

const schema = yup.object({
  aadhaar: yup.string().required("Aadhaar number is required.")
    .test("length-check", "Aadhaar must be exactly 12 digits.", (value) => {
      if (!value) return false;
      const digits = value.replace(/\D/g, "");
      return digits.length === 12;
    })
    .test("start-check", "Aadhaar cannot start with 0 or 1.", (value) => {
      if (!value) return false;
      const digits = value.replace(/\D/g, "");
      if (digits.length < 1) return true;
      return !/^[01]/.test(digits);
    }),
  pan: yup.string().required("PAN is required").matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN format. Example: ABCDE1234F"),
  hasEsic: yup.boolean().required("Please select if ESIC is applicable"),
  account_Number: yup.string().required("Account number is required.").matches(/^\d+$/, "Account number must contain only digits.").min(9, "Account number must be at least 9 digits.").max(18, "Account number cannot exceed 18 digits."),
  ifsc_Code: yup.string().required("IFSC Code is required").matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Invalid IFSC Code. Example: SBIN0001234"),
  bank_Name: yup.number().required("Bank Name is required"),
  branch_Name: yup.string().required("Branch name is required.").matches(/^[A-Za-z\s&]+$/, "Branch name can only contain letters, spaces, and '&'").min(2, "Branch name must be at least 2 characters").max(50, "Branch name cannot exceed 50 characters"),
});

const IdentityBank: React.FC<IdentityBankProps> = ({
  nextStep,
  prevStep,
  identity,
  setIdentity,
  employeeId,
}) => {
  const [loading, setLoading] = useState(false);
  const [bankOptions, setBankOptions] = useState<BOption[]>([]);
  const [banksLoaded, setBanksLoaded] = useState(false);
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);

  const { register, handleSubmit, control, formState: { errors }, setValue, reset, watch, } = useForm<IdentityBankForm>({
    resolver: yupResolver(schema),
    defaultValues: identity,
    mode: "onSubmit",
  });

  useEffect(() => {
    reset(identity);
  }, [identity, reset]);

  const loadBanks = async () => {
    if (banksLoaded) return;

    const response = await fetchApi("DropDown/bankName", "GET", null, "", "");
    if (response) {
      setBankOptions(response);
      setBanksLoaded(true);
    }
  };

  useEffect(() => {

    loadBanks()
  }, [])


  /** Submit handler */
  const onSubmit = async (formValues: IdentityBankForm) => {
    try {
      if (!employeeId) {
        addToast({ message: "Employee ID not found", status: "error" });
        return;
      }
      if (formValues.uan && !/^\d{12}$/.test(formValues.uan)) {
        addToast({ message: "UAN must be exactly 12 digits", status: "error" });
        return;
      }

      if (formValues.hasEsic && (!formValues.esic || formValues.esic.length === 0)) {
        addToast({ message: "ESIC number is required", status: "error" });
        return;
      }
      if (formValues.hasEsic && !/^\d{10}$/.test(formValues.esic!)) {
        addToast({ message: "ESIC must be exactly 10 digits", status: "error" });
        return;
      }
      setLoading(true);
      setIdentity(formValues);
      const payload = {
        empId: employeeId,
        aadhaar: formValues.aadhaar,
        pan: formValues.pan,
        uan: formValues.uan,
        hasEsic: formValues.hasEsic,
        esic: formValues.esic,
        account_Number: formValues.account_Number,
        ifsc_Code: formValues.ifsc_Code,
        bank_Name: formValues.bank_Name,
        branch_Name: formValues.branch_Name,
      };

      const response = await fetchApi("Hrlense_Employee/identityBank", "POST", payload, null, "Identity bank added successfully");
      if (response) nextStep();
    } catch (error: any) {
      addToast({ message: error.message || "Something went wrong", status: "error" });
    } finally {
      setLoading(false);
    }
  };


  const hasEsicValue = watch("hasEsic");

  return (
    <MyDiv>
      <Box m={6} as="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={12}>
          {/* Left Column */}
          <GridItem>
            {/* Aadhaar */}
            <FormControl mb={4} isInvalid={!!errors.aadhaar} isRequired>
              <FormLabel>Aadhaar Number</FormLabel>
              <Input
                type="text"
                placeholder="Enter Aadhaar Number"
                maxLength={14}
                {...register("aadhaar")}
                onChange={(e) => {
                  let val = e.target.value.replace(/\D/g, "").slice(0, 12);
                  val = val.replace(/(\d{4})(\d{4})(\d{0,4})/, (_, p1, p2, p3) =>
                    [p1, p2, p3].filter(Boolean).join(" ")
                  );
                  setValue("aadhaar", val, { shouldValidate: true });
                }}
              />
              <FormErrorMessage>{errors.aadhaar?.message}</FormErrorMessage>
            </FormControl>

            {/* PAN */}
            <FormControl mb={4} isInvalid={!!errors.pan} isRequired>
              <FormLabel>PAN Number</FormLabel>
              <Input
                type="text"
                placeholder="Enter PAN number"
                maxLength={10}
                {...register("pan")}
                onChange={(e: any) => {
                  const val = e.target.value.toUpperCase().slice(0, 10);
                  setValue("pan", val, { shouldValidate: true });
                }}
              />
              <FormErrorMessage>{errors.pan?.message}</FormErrorMessage>
            </FormControl>

            {/* UAN */}
            <FormControl mb={4} isInvalid={!!errors.uan}>
              <FormLabel>UAN Number</FormLabel>
              <Input
                type="text"
                placeholder="Enter UAN number"
                maxLength={12}
                {...register("uan")}
                onChange={(e: any) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 12);
                  setValue("uan", val, { shouldValidate: false });
                }}
              />
              <FormErrorMessage>{errors.uan?.message}</FormErrorMessage>
            </FormControl>

            {/* ESIC */}
              <FormControl mb={4} isInvalid={!!errors.hasEsic} isRequired>
              <FormLabel>ESIC Applicable?</FormLabel>
              <Controller
                name="hasEsic"
                control={control}
                render={({ field }) => (
                  <Select
                    placeholder="Select"
                    value={field.value === true ? "true" : field.value === false ? "false" : ""}
                    onChange={(e) => field.onChange(e.target.value === "true")}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </Select>
                )}
              />
              <FormErrorMessage>{errors.hasEsic?.message}</FormErrorMessage>
            </FormControl>

            {hasEsicValue && (
              <FormControl mb={4} isInvalid={!!errors.esic} isRequired>
                <FormLabel>ESIC Number</FormLabel>
                <Input
                  type="text"
                  placeholder="Enter ESIC number"
                  maxLength={10}
                  {...register("esic")}
                  onChange={(e: any) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setValue("esic", val, { shouldValidate: false });
                  }}
                />
                <FormErrorMessage>{errors.esic?.message}</FormErrorMessage>
              </FormControl>
            )}
          </GridItem>

          <GridItem>
            <FormControl mb={4} isInvalid={!!errors.bank_Name} isRequired>
              <FormLabel>Bank Name</FormLabel>
              <Controller
                name="bank_Name"
                control={control}
                render={({ field }) => (
                  <Select
                    placeholder="Select Bank Name"
                    value={field.value ? field.value.toString() : ""}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    onClick={loadBanks}
                  >
                    {bankOptions.map((option) => (
                      <option key={option.id} value={String(option.id)}>
                        {option.bank_Name}
                      </option>
                    ))}
                  </Select>
                )}
              />
              <FormErrorMessage>{errors.bank_Name?.message}</FormErrorMessage>
            </FormControl>
            <FormControl mb={4} isInvalid={!!errors.account_Number} isRequired>
              <FormLabel>Account Number</FormLabel>
              <Input
                type="text"
                placeholder="Enter Account Number"
                maxLength={18}
                {...register("account_Number")}
                onChange={(e: any) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 18);
                  setValue("account_Number", val, { shouldValidate: true });
                }}
              />
              <FormErrorMessage>{errors.account_Number?.message}</FormErrorMessage>
            </FormControl>

            <FormControl mb={4} isInvalid={!!errors.ifsc_Code} isRequired>
              <FormLabel>IFSC Code</FormLabel>
              <Input
                type="text"
                placeholder="Enter IFSC Code"
                maxLength={11}
                {...register("ifsc_Code")}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase().slice(0, 11);
                  setValue("ifsc_Code", val, { shouldValidate: true });
                }}
              />
              <FormErrorMessage>{errors.ifsc_Code?.message}</FormErrorMessage>
            </FormControl>

            <FormControl mb={4} isInvalid={!!errors.branch_Name} isRequired>
              <FormLabel>Branch</FormLabel>
              <Input
                type="text"
                placeholder="Enter Branch Name"
                {...register("branch_Name")}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^a-zA-Z\s&]/g, "").slice(0, 50);
                  setValue("branch_Name", val, { shouldValidate: true });
                }}

              />
              <FormErrorMessage>{errors.branch_Name?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

        </Grid>

        <Box mt={6} display="flex" justifyContent="space-between">
          <Button onClick={prevStep} colorScheme="gray" leftIcon={<ArrowBackIcon boxSize={4} />}>
            Previous
          </Button>
          <Button type="submit" isLoading={loading} colorScheme="green" rightIcon={<ArrowForwardIcon boxSize={4} />}>
            Save & Next
          </Button>
        </Box>
      </Box>
    </MyDiv>
  );
};

export default IdentityBank;
